import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  ClaimDecision,
  DuplicatePair,
  PairStatus,
  ProfileDetail,
  QueueFilters,
  QueueResponse,
  StatsResponse,
} from '@/client/types/profiles';

const BASE = '/api/admin/v2/profiles';

const qs = (params: Record<string, string | number | undefined>) => {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length ? `?${parts.join('&')}` : '';
};

export const profileKeys = {
  all: ['profiles'] as const,
  queue: (params: QueueFilters) => [...profileKeys.all, 'queue', params] as const,
  stats: (days: number) => [...profileKeys.all, 'stats', days] as const,
  duplicates: (status: string, offset: number, limit: number) =>
    [...profileKeys.all, 'duplicates', status, offset, limit] as const,
  profile: (id: number) => [...profileKeys.all, 'profile', id] as const,
};

/** Admin v2 wraps payloads in `{ status, data }`; accept either shape. */
const unwrap = <T>(raw: unknown): T => {
  if (raw != null && typeof raw === 'object' && 'data' in raw && 'status' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
};

const useGet = () => {
  const request = useClientStore((s) => s.request);
  const fn = withErrorHandling(
    async (path: string): Promise<unknown> =>
      unwrap<unknown>(await request<unknown>(`${BASE}${path}`)),
    'Failed to load profiles',
  );
  return fn as unknown as <T>(path: string) => Promise<T>;
};

export const useProfileQueueQuery = (params: QueueFilters, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: profileKeys.queue(params),
    queryFn: () => get<QueueResponse>(`/queue${qs({ ...params })}`),
    enabled,
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useProfileStatsQuery = (days: number, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: profileKeys.stats(days),
    queryFn: () => get<StatsResponse>(`/stats${qs({ days })}`),
    enabled,
    staleTime: 1000 * 60,
    gcTime: DEFAULT_GC_TIME,
  });
};

export const useDuplicatesQuery = (status: PairStatus | '', offset: number, limit: number, enabled = true) => {
  const get = useGet();
  return useQuery({
    queryKey: profileKeys.duplicates(status, offset, limit),
    queryFn: async () =>
      (await get<{ duplicates: DuplicatePair[] }>(`/duplicates${qs({ status, offset, limit })}`)).duplicates,
    enabled,
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useProfileQuery = (id: number | null) => {
  const get = useGet();
  return useQuery({
    queryKey: profileKeys.profile(id ?? 0),
    queryFn: () => get<ProfileDetail>(`/${id}`),
    enabled: id != null,
    staleTime: 1000 * 30,
    gcTime: DEFAULT_GC_TIME,
  });
};

/** One decision for one or many facts. Every read is refreshed afterwards. */
export const useClaimReviewMutation = () => {
  const request = useClientStore((s) => s.request);
  const queryClient = useQueryClient();
  const fn = withErrorHandling(
    async (vars: { ids: number[]; status: ClaimDecision }): Promise<{ changed: number }> =>
      unwrap<{ changed: number }>(await request<unknown>(`${BASE}/review`, 'POST', vars)),
    'Failed to record the decision',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: profileKeys.all }),
  });
};

/** "same" merges the other profile into `keep`; "distinct" records that they are two. */
export const useDuplicateDecisionMutation = () => {
  const request = useClientStore((s) => s.request);
  const queryClient = useQueryClient();
  const fn = withErrorHandling(
    async (vars: { id: number; decision: 'same' | 'distinct'; keep?: number }): Promise<unknown> =>
      unwrap<unknown>(
        await request<unknown>(`${BASE}/duplicates/${vars.id}`, 'POST', {
          decision: vars.decision,
          keep: vars.keep,
        }),
      ),
    'Failed to record the decision',
  );
  return useMutation({
    mutationFn: fn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: profileKeys.all }),
  });
};
